import { EventTimeline, MatrixClient } from 'matrix-js-sdk/src/matrix';

import { MeetingResponse } from 'api/meedioBackend/types';
import { getMeetingElapsedStartIso } from 'modules/Meetings/utils/getMeetingElapsedStartIso';
import { getCallSessionDetails } from 'modules/RoomsListItem/utils';
import { CUSTOM_MEEDIO_EVENTS } from 'utils/matrixUtils';
import {
  getCurrentOrNextOccurrenceWindow,
  getMostRecentPastOccurrenceWindow,
  isInOccurrenceWindow,
  JOIN_PREROOM_THRESHOLD_MS,
  OccurrenceWindow,
} from 'utils/occurrenceWindow';

export const getMessengerCallLiveRoomId = (matrixClient: MatrixClient, messengerRoomId: string): string | null => {
  const content = matrixClient
    .getRoom(messengerRoomId)
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CUSTOM_MEEDIO_EVENTS.MESSENGER_CALL, '')
    ?.getContent();
  return (content?.call_room_id as string | undefined) ?? null;
};

export const isMeetingSessionOpenOnTimeline = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  now: Date = new Date()
): boolean => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  if (!messengerRoom) return false;

  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  // BUG-072: widen the recurring floor by the pre-room Join threshold so a
  // MEETING_STARTED emitted up to JOIN_PREROOM_THRESHOLD_MS before the occurrence
  // start (an early "Start now") still counts as an open session. Without this an
  // early-started recurring occurrence was invisible in the sidebar Active list and
  // banner until its scheduled start time, even though the meeting was already live.
  const floorTs =
    meeting.is_recurring && window ? window.startMs - JOIN_PREROOM_THRESHOLD_MS : Number.NEGATIVE_INFINITY;

  const events = messengerRoom.getLiveTimeline().getEvents();
  let latestStartTs = Number.NEGATIVE_INFINITY;
  for (const ev of events) {
    // BUG-072: this helper is now consulted in the sidebar render hot path for
    // every meeting (early-start discovery), so guard against any timeline entry
    // that does not expose getTs rather than throwing during render.
    if (typeof ev.getTs !== 'function' || ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_STARTED) continue;
    const content = ev.getContent() ?? {};
    if (content.meeting_room_id !== meeting.matrix_room_id) continue;
    if (content.meeting_id && content.meeting_id !== meeting.id) continue;
    const ts = ev.getTs();
    if (ts >= floorTs && ts > latestStartTs) latestStartTs = ts;
  }
  if (!Number.isFinite(latestStartTs)) return false;

  // BUG-002: MESSENGER_CALL_STATE.ended on the call room is the authoritative
  // "session over" signal. A spurious peer re-announce (MEETING_STARTED) at the
  // scheduled wall-clock start after an early host end makes latestStartTs newer
  // than the MEETING_ENDED ts, hiding the end from the events.some() check below.
  // Reading the state event directly avoids that race.
  const callRoom = matrixClient.getRoom(meeting.matrix_room_id);
  const callRoomEnded =
    callRoom
      ?.getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      ?.getStateEvents(CUSTOM_MEEDIO_EVENTS.MESSENGER_CALL_STATE, '')
      ?.getContent()?.ended === true;
  if (callRoomEnded) return false;

  return !events.some((ev) => {
    if (typeof ev.getTs !== 'function' || ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_ENDED) return false;
    const content = ev.getContent() ?? {};
    if (content.meeting_room_id !== meeting.matrix_room_id) return false;
    if (content.meeting_id && content.meeting_id !== meeting.id) return false;
    return ev.getTs() > latestStartTs;
  });
};

export const isCancelledCurrentOccurrenceStillLive = (
  meeting: MeetingResponse,
  matrixClient: MatrixClient,
  messengerRoomId: string,
  now: Date,
  messengerCallLiveRoomId: string | null,
  locallyEndedIds?: Set<string>
): boolean => {
  if (!meeting.cancelled_at) return false;
  if (locallyEndedIds?.has(meeting.id)) return false;
  if (meeting.ended_at) return false;
  if (!isInOccurrenceWindow(meeting, now)) return false;
  if (hasMeetingEndedOnTimeline(matrixClient, messengerRoomId, meeting, now)) return false;

  if (messengerCallLiveRoomId === meeting.matrix_room_id) return true;
  if (isMeetingSessionOpenOnTimeline(matrixClient, messengerRoomId, meeting, now)) return true;
  if (meeting.started_at) return true;

  const callRoom = matrixClient.getRoom(meeting.matrix_room_id);
  return !!callRoom && getCallSessionDetails(matrixClient, callRoom).isActive;
};

export const shouldShowMeetingInActiveList = (
  meeting: MeetingResponse | null,
  matrixClient: MatrixClient,
  messengerRoomId: string,
  now: Date,
  messengerCallLiveRoomId: string | null,
  locallyEndedIds?: Set<string>
): boolean => {
  if (!meeting) return true;
  if (!meeting.cancelled_at) return true;
  return isCancelledCurrentOccurrenceStillLive(
    meeting,
    matrixClient,
    messengerRoomId,
    now,
    messengerCallLiveRoomId,
    locallyEndedIds
  );
};

// Mirrors the banner's `useActiveMeetings` pass-2 window predicate: a scheduled
// meeting only stays "active" via the timeline (MEETING_STARTED with no
// MEETING_ENDED) while its occurrence window is open OR closed within the grace
// period. After that, the only authoritative "still running" signal is an active
// RTC session. Without this gate the sidebar pinned meetings whose MEETING_ENDED
// was never paginated into the local timeline as Active forever (BUG-017).
export const TIMELINE_ACTIVE_GRACE_MS = 30 * 60 * 1000;

export const isWithinTimelineActiveWindow = (meeting: MeetingResponse, now: Date = new Date()): boolean => {
  if (isInOccurrenceWindow(meeting, now)) return true;
  const nowMs = now.getTime();
  const pastWindow = getMostRecentPastOccurrenceWindow(meeting, now);
  if (pastWindow) return nowMs < pastWindow.endMs + TIMELINE_ACTIVE_GRACE_MS;
  const endsAtMs = meeting.ends_at ? Date.parse(meeting.ends_at) : null;
  if (endsAtMs) return nowMs < endsAtMs + TIMELINE_ACTIVE_GRACE_MS;
  // No window metadata — let the timeline decide (matches useActiveMeetings).
  return true;
};

export const getNextOccurrenceDate = (meeting: MeetingResponse, now: Date): Date => {
  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  if (window) return new Date(window.startMs);
  return meeting.scheduled_at ? new Date(meeting.scheduled_at) : now;
};

export const getNextOccurrenceEndDate = (meeting: MeetingResponse, now: Date): Date => {
  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  if (window) return new Date(window.endMs);
  return meeting.ends_at ? new Date(meeting.ends_at) : now;
};

export const hasFutureOccurrence = (meeting: MeetingResponse, now: Date): boolean => {
  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  if (!window) return false;
  return window.endMs > now.getTime();
};

export const getNextOccurrenceWindowAfterCurrent = (
  meeting: MeetingResponse,
  now: Date
): ReturnType<typeof getCurrentOrNextOccurrenceWindow> => {
  if (!meeting.is_recurring) return null;
  const currentWindow = getCurrentOrNextOccurrenceWindow(meeting, now);
  if (!currentWindow) return null;
  return getCurrentOrNextOccurrenceWindow(meeting, new Date(currentWindow.endMs + 1));
};

export const getScheduledElapsedStartIso = (
  meeting: MeetingResponse | null,
  messengerCallStartedAt: string | null,
  now: Date = new Date()
): string | null => getMeetingElapsedStartIso(meeting, { now, messengerCallStartedAt });

/**
 * Returns true when a meeting was explicitly started early (via "Start meeting
 * now") inside the pre-room window and the session was then closed before the
 * scheduled window opened — i.e. the last participant left and a non-delayed
 * MEETING_ENDED was emitted without `ended_by`.
 *
 * #8136 (Wave 3): "Start now → leave" emits MEETING_ENDED without `ended_by`,
 * so hasExplicitHostEndBeforeWindow misses it. Past tab already picks these up
 * via getMeetingActualEndedAt (any non-delayed end). This helper closes the
 * gap in isOccurrenceEndedForDisplay so Incoming agrees.
 *
 * Invariant preserved (#7934 / UT-001-2): if there is no MEETING_STARTED in
 * the same pre-room range, the MEETING_ENDED is an incidental pre-window leave
 * on a NOT-yet-started occurrence — leave the occurrence intact.
 */
const hasPreWindowStartedSessionEnded = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  window: OccurrenceWindow
): boolean => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  if (!messengerRoom) return false;

  const floorTs = window.startMs - JOIN_PREROOM_THRESHOLD_MS;
  const events = messengerRoom.getLiveTimeline().getEvents();

  // Find the latest MEETING_STARTED for this meeting inside the pre-room window.
  let latestStartTs = Number.NEGATIVE_INFINITY;
  for (const ev of events) {
    if (typeof ev.getTs !== 'function' || ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_STARTED) continue;
    const content = ev.getContent() ?? {};
    if ((content.meeting_room_id as string | undefined) !== meeting.matrix_room_id) continue;
    if (content.meeting_id && content.meeting_id !== meeting.id) continue;
    const ts = ev.getTs();
    if (ts < floorTs || ts >= window.startMs) continue;
    if (ts > latestStartTs) latestStartTs = ts;
  }

  // No start found inside the pre-room window → incidental leave; leave intact.
  if (!Number.isFinite(latestStartTs)) return false;

  // A non-delayed end that arrived AFTER the start (but still before the window)
  // means the early session was properly closed — treat the occurrence as ended.
  return events.some((ev) => {
    if (typeof ev.getTs !== 'function' || ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_ENDED) return false;
    const content = ev.getContent() ?? {};
    if ((content.meeting_room_id as string | undefined) !== meeting.matrix_room_id) return false;
    if (content.meeting_id && content.meeting_id !== meeting.id) return false;
    const ts = ev.getTs();
    if (ts <= latestStartTs || ts >= window.startMs) return false;
    return (content.source as string | undefined) !== 'delayed_event';
  });
};

/**
 * Returns true when an explicit host "End meeting" landed in the pre-room window
 * (`[window.startMs - JOIN_PREROOM_THRESHOLD_MS, window.startMs)`) before the
 * occurrence opened. Distinguished from an incidental early leave by `ended_by`
 * being present and `source` not being the homeserver delayed cleanup.
 *
 * BUG-001: a host who ends a scheduled meeting before its scheduled start used to
 * keep the occurrence joinable in the sidebar/card, because the pre-window branch
 * returned false unconditionally. This bounded scan ends it for display while
 * still ignoring incidental early leaves (#7934) and stale delayed events.
 *
 * Note: this cannot reuse `hasMeetingEndedOnTimeline` — that helper uses an
 * unbounded floor for non-recurring meetings and does not require `ended_by`, so
 * it would also fire on incidental leaves and on ends far outside the pre-room.
 */
const hasExplicitHostEndBeforeWindow = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  window: OccurrenceWindow
): boolean => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  if (!messengerRoom) return false;

  const floorTs = window.startMs - JOIN_PREROOM_THRESHOLD_MS;

  return messengerRoom
    .getLiveTimeline()
    .getEvents()
    .some((ev) => {
      if (typeof ev.getTs !== 'function' || ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_ENDED) return false;
      const content = ev.getContent() ?? {};
      if (content.meeting_room_id !== meeting.matrix_room_id) return false;
      if (content.meeting_id && content.meeting_id !== meeting.id) return false;
      const ts = ev.getTs();
      if (ts < floorTs || ts >= window.startMs) return false;
      // ended_by → explicit host end (not an incidental leave/hangup, #7934).
      if (!content.ended_by) return false;
      return (content.source as string | undefined) !== 'delayed_event';
    });
};

/**
 * Single display rule for "should the UI treat this occurrence as ended?".
 *
 * Fixes the early-join/leave bug (#7934): a MEETING_ENDED event posted before
 * the occurrence window opens must not consume the upcoming occurrence unless
 * the meeting was genuinely started early. Steps:
 *  1. Cancelled meetings → always ended for display.
 *  2. Resolve the current/next occurrence window.
 *  3. No window (instant rooms) → delegate to hasMeetingEndedOnTimeline.
 *  4. now < window.startMs → ended if:
 *     a. hasExplicitHostEndBeforeWindow — host clicked End (ended_by, BUG-001)
 *     b. hasPreWindowStartedSessionEnded — "Start now" + leave: MEETING_STARTED
 *        followed by non-delayed MEETING_ENDED in the pre-room window (#8136 W3).
 *     Incidental leave with no preceding MEETING_STARTED stays intact (#7934).
 *  5. now >= window.endMs → true (window has elapsed, treat as ended).
 *  6. Inside window → delegate to hasMeetingEndedOnTimeline.
 */
export const isOccurrenceEndedForDisplay = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  now: Date = new Date()
): boolean => {
  if (meeting.cancelled_at !== null) return true;

  const window = getCurrentOrNextOccurrenceWindow(meeting, now);

  if (window === null) {
    return hasMeetingEndedOnTimeline(matrixClient, messengerRoomId, meeting, now);
  }

  if (now.getTime() < window.startMs) {
    return (
      hasExplicitHostEndBeforeWindow(matrixClient, messengerRoomId, meeting, window) ||
      hasPreWindowStartedSessionEnded(matrixClient, messengerRoomId, meeting, window)
    );
  }

  if (now.getTime() >= window.endMs) return true;

  if (hasMeetingEndedOnTimeline(matrixClient, messengerRoomId, meeting, now)) return true;

  // BUG-035: backend `ended_at` is the authoritative fallback when the MEETING_ENDED
  // timeline event hasn't paginated into the local room (e.g. a user who wasn't in the
  // chat when it ended). Only for non-recurring meetings — recurring `ended_at` is
  // per-series-null. Counts only when the end falls within the current occurrence window.
  if (!meeting.is_recurring && meeting.ended_at) {
    const endedAtMs = Date.parse(meeting.ended_at);
    if (!Number.isNaN(endedAtMs) && endedAtMs >= window.startMs) return true;
  }

  return false;
};

/**
 * Single read-side predicate for "should any live-meeting UI (active banner,
 * sidebar Incoming/Active, Join now) treat this scheduled occurrence as still
 * running?". Unifies the signals that the banner, sidebar, timeline, and pre-room
 * each used to derive independently — the root cause of #8136, where the banner
 * showed "live" while the timeline showed "ended".
 *
 * Ended checks (authoritative) run first and are NOT overridable by RTC:
 *  1. Non-recurring `meeting.ended_at` — backend end (recurring is per-series-null).
 *  2. `MESSENGER_CALL_STATE.ended` on the call room — state event, no pagination.
 *  3. `isOccurrenceEndedForDisplay` — timeline + window + backend fallback (the same
 *     rule the timeline cards use, so banner/sidebar can never disagree with it).
 *
 * Live signals (only consulted when 1–3 say NOT ended):
 *  4. Active RTC session with > 0 memberships.
 *  5. An open `MEETING_STARTED` session within the timeline active window.
 *  6. Inside the occurrence window — a scheduled meeting is live even with 0
 *     participants and no `MEETING_STARTED` yet (mirrors sidebar `windowOpenMeetings`).
 *
 * Replaces the banner-only `isCurrentlyScheduledOpen` shortcut, which gated only on
 * the weaker `hasMeetingEndedOnTimeline` and so kept the banner alive after an early
 * end / call-state end / backend end (#8136 H1, AC-8136-01/02/03/07).
 */
export const isMeetingLiveForMessengerUI = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  now: Date = new Date()
): boolean => {
  if (!meeting.is_recurring && meeting.ended_at) return false;

  const callRoom = matrixClient.getRoom(meeting.matrix_room_id);
  const callRoomEnded =
    callRoom
      ?.getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      ?.getStateEvents(CUSTOM_MEEDIO_EVENTS.MESSENGER_CALL_STATE, '')
      ?.getContent()?.ended === true;
  if (callRoomEnded) return false;

  if (isOccurrenceEndedForDisplay(matrixClient, messengerRoomId, meeting, now)) return false;

  const rtcSession = callRoom ? matrixClient.matrixRTC.getActiveRoomSession(callRoom) : null;
  if (!!rtcSession && rtcSession.memberships.length > 0) return true;

  if (
    isWithinTimelineActiveWindow(meeting, now) &&
    isMeetingSessionOpenOnTimeline(matrixClient, messengerRoomId, meeting, now)
  ) {
    return true;
  }

  return isInOccurrenceWindow(meeting, now);
};

/**
 * Returns true when there is an explicit end (host pressed End, last participant
 * hung up, or watchdog cleanup) on the messenger timeline. Homeserver-side delayed
 * cleanup events (`source: 'delayed_event'`) are ignored — they fire as a stale
 * fallback after the cancel HTTP fails, and counting them would prematurely conclude
 * the meeting.
 *
 * BUG-012: previously required `ended_by` to be present, which excluded the
 * hangup-path and watchdog-path `MEETING_ENDED` events after BUG-007 removed
 * `ended_by` from those emits. The correct filter is `source !== 'delayed_event'`.
 */
export const hasMeetingEndedOnTimeline = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  now: Date = new Date()
): boolean => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  if (!messengerRoom) return false;

  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  const floorTs = meeting.is_recurring && window ? window.startMs : Number.NEGATIVE_INFINITY;

  return messengerRoom
    .getLiveTimeline()
    .getEvents()
    .some((ev) => {
      if (ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_ENDED) return false;
      const content = ev.getContent() ?? {};
      if (content.meeting_room_id !== meeting.matrix_room_id) return false;
      if (content.meeting_id && content.meeting_id !== meeting.id) return false;
      if (ev.getTs() < floorTs) return false;
      const source = content.source as string | undefined;
      // BUG-012: any non-delayed end counts — ended_by is only set by the host
      // path (MeetingSidebarView) after BUG-007 removed it from hangup/watchdog emits.
      return source !== 'delayed_event';
    });
};

/**
 * Returns the `ended_at` from the first explicit end on the timeline (host clicked
 * End from sidebar, or last participant hung up). Used to surface the meeting in
 * Past with its real end time instead of the scheduled `ends_at`. Homeserver-side
 * delayed cleanup events are ignored.
 */
export const getMeetingActualEndedAt = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meeting: MeetingResponse,
  now: Date = new Date()
): string | null => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  if (!messengerRoom) return null;

  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  const floorTs = meeting.is_recurring && window ? window.startMs : Number.NEGATIVE_INFINITY;

  for (const ev of messengerRoom.getLiveTimeline().getEvents()) {
    if (ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_ENDED) continue;
    const content = ev.getContent() ?? {};
    if (content.meeting_room_id !== meeting.matrix_room_id) continue;
    if (content.meeting_id && content.meeting_id !== meeting.id) continue;
    if (ev.getTs() < floorTs) continue;
    const source = content.source as string | undefined;
    // BUG-012: accept any non-delayed end — not just host-end (ended_by present).
    if (source === 'delayed_event') continue;
    return (content.ended_at as string | undefined) ?? null;
  }
  return null;
};

export const getMessengerMeetingStartedAt = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  meetingRoomId: string
): string | null => {
  const events = matrixClient.getRoom(messengerRoomId)?.getLiveTimeline().getEvents() ?? [];
  let latestMs = Number.NEGATIVE_INFINITY;
  let latestIso: string | null = null;
  for (const ev of events) {
    if (ev.getType() !== CUSTOM_MEEDIO_EVENTS.MEETING_STARTED) continue;
    if ((ev.getContent()?.meeting_room_id as string | undefined) !== meetingRoomId) continue;
    const startedAt = ev.getContent()?.started_at as string | undefined;
    if (!startedAt) continue;
    const ms = Date.parse(startedAt);
    if (Number.isFinite(ms) && ms > latestMs) {
      latestMs = ms;
      latestIso = startedAt;
    }
  }
  return latestIso;
};
